/* eslint-disable no-console */

const BADGE_BG_COLOR = '#EB2330';      // Figma: Priority Palette/500/Highest
const BADGE_TEXT_COLOR = '#FFFFFF';    // Figma: Colour Palette/White 500
const BADGE_BORDER_COLOR = '#000000';  // Figma: 0.5px border, all sides

/**
 * Badge label for an unread count. Null means "no badge".
 * Display is capped at "99" (ticket: mockup maximum count).
 */
export const getBadgeText = (count) => {
    if (!count || count <= 0) return null;
    return count > 99 ? '99' : String(Math.floor(count));
};

/**
 * Draws the badge in DEVICE pixels for the given favicon size.
 * Figma "Notification counter" pill (Top-Bar node 3546-196227, authored on a
 * 16px favicon), anchored TOP-RIGHT of the favicon (matching Rocket.Chat's
 * badge alignment): fully-rounded pill with true semicircle ends, height 11px,
 * full favicon width for two digits (one digit hugs to a circle), fill
 * #EB2330, 0.5px black border, count centered in the pill in Roboto Bold
 * white.
 *
 * Device pixels are load-bearing: an earlier version drew on a size-scaled
 * context with a fractional font (bold 0.625px) and centered the digits via
 * measureText glyph bounds — FreeType (Linux/ChromeOS) quantizes font metrics
 * at sub-pixel sizes (ascent came back 1.000 instead of ~0.5), pushing the
 * count out of the pill. Whole-pixel fonts (10px at 16, 20px at 32) measure
 * reliably on every font engine.
 */
export const drawBadge = (text, context, size) => {
    const border = (0.5 / 16) * size;
    const h = (11 / 16) * size;
    const y = border / 2;                           // top edge, stroke not clipped
    const w = text.length > 1 ? size - border : h;  // hug: two digits span the favicon width
    const x = size - w - border / 2;                // right-aligned, stroke not clipped
    const r = h / 2;

    context.beginPath();
    context.moveTo(x + r, y);
    context.lineTo(x + w - r, y);
    context.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
    context.lineTo(x + r, y + h);
    context.arc(x + r, y + r, r, Math.PI / 2, (3 * Math.PI) / 2);
    context.closePath();
    context.fillStyle = BADGE_BG_COLOR;
    context.fill();
    context.lineWidth = border;
    context.strokeStyle = BADGE_BORDER_COLOR;
    context.stroke();

    context.font = `bold ${(10 / 16) * size}px Roboto, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';
    context.fillStyle = BADGE_TEXT_COLOR;
    // textBaseline 'middle' centers the font's em box, not the visible digits,
    // leaving the count optically off-center — measure the real glyph bounds
    // and center those in the pill instead.
    const metrics = context.measureText(text);
    const ascent = metrics.actualBoundingBoxAscent || 0;
    const descent = metrics.actualBoundingBoxDescent || 0;
    context.fillText(text, x + w / 2, y + h / 2 + (ascent - descent) / 2);
};

// Chrome renders tab favicons at 16 DIP with 1x and 2x scale factors only, and
// resamples (Lanczos3) any candidate that is not an exact pixel match. It also
// picks a single <link> — multiple links with different sizes are NOT combined.
// So the only way to get an untouched render on both standard-DPI (Windows/
// Linux/ChromeOS tabs, 16px) and hiDPI (macOS Retina, 32px) displays is one
// multi-frame ICO carrying a native render per size.
const BADGE_SIZES = [16, 32];

/**
 * Packs PNG frames ({size, bytes}) into a single ICO file (ICONDIR header +
 * one ICONDIRENTRY per frame + payloads). PNG-compressed ICO frames are
 * supported by every browser that supports ICO favicons.
 */
export const packIco = (frames) => {
    const directorySize = 6 + frames.length * 16;
    const total = directorySize + frames.reduce((sum, frame) => sum + frame.bytes.length, 0);
    const ico = new Uint8Array(total);
    const view = new DataView(ico.buffer);
    view.setUint16(0, 0, true);              // reserved
    view.setUint16(2, 1, true);              // type: icon
    view.setUint16(4, frames.length, true);  // frame count
    let offset = directorySize;
    frames.forEach(({ size, bytes }, index) => {
        const entry = 6 + index * 16;
        ico[entry] = size;                   // width (0 would mean 256)
        ico[entry + 1] = size;               // height
        ico[entry + 2] = 0;                  // palette colors
        ico[entry + 3] = 0;                  // reserved
        view.setUint16(entry + 4, 1, true);  // color planes
        view.setUint16(entry + 6, 32, true); // bits per pixel
        view.setUint32(entry + 8, bytes.length, true);
        view.setUint32(entry + 12, offset, true);
        ico.set(bytes, offset);
        offset += bytes.length;
    });
    return ico;
};

const dataUrlToBytes = (dataUrl) => {
    const binary = atob(dataUrl.slice(dataUrl.indexOf(',') + 1));
    return Uint8Array.from(binary, char => char.charCodeAt(0));
};

const bytesToBase64 = (bytes) => {
    let binary = '';
    // String.fromCharCode(...allBytes) overflows the argument limit on big arrays
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
};

const getIconLinks = () => {
    const links = Array.from(document.head.getElementsByTagName('link'))
        .filter(l => /(^|\s)icon(\s|$)/i.test(l.getAttribute('rel') || ''));
    if (links.length === 0) {
        const created = document.createElement('link');
        created.setAttribute('rel', 'icon');
        created.setAttribute('href', '/favicon.ico');
        document.head.appendChild(created);
        links.push(created);
    }
    return links;
};

const loadImage = url => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load base favicon "${url}"`));
    img.src = url;
});

/**
 * Creates the favicon badge manager. Returns updateFavicon(count):
 * count > 0 draws the badge, count <= 0 restores the original favicon.
 * All failures degrade to a plain favicon; nothing throws to the caller.
 */
export const manageFavicon = () => {
    let pendingCount;
    let applyCount = (count) => { pendingCount = count; };

    const init = async () => {
        const links = getIconLinks();
        const originalHrefs = links.map(l => l.getAttribute('href') || '/favicon.ico');
        const originalTypes = links.map(l => l.getAttribute('type'));
        const img = await loadImage(originalHrefs[originalHrefs.length - 1]);
        const renderers = BADGE_SIZES.map((size) => {
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const context = canvas.getContext('2d');
            if (!context) {
                throw new Error('canvas 2d context unavailable');
            }
            return { size, canvas, context };
        });

        applyCount = (count) => {
            try {
                const text = getBadgeText(count);
                if (!text) {
                    links.forEach((l, i) => {
                        l.setAttribute('href', originalHrefs[i]);
                        if (originalTypes[i] === null) {
                            l.removeAttribute('type');
                        } else {
                            l.setAttribute('type', originalTypes[i]);
                        }
                    });
                    return;
                }
                const frames = renderers.map(({ size, canvas, context }) => {
                    context.setTransform(1, 0, 0, 1, 0, 0);
                    context.clearRect(0, 0, size, size);
                    context.drawImage(img, 0, 0, size, size);
                    drawBadge(text, context, size);
                    return { size, bytes: dataUrlToBytes(canvas.toDataURL('image/png')) };
                });
                const url = `data:image/x-icon;base64,${bytesToBase64(packIco(frames))}`;
                links.forEach((l) => {
                    l.setAttribute('href', url);
                    l.setAttribute('type', 'image/x-icon');
                });
            } catch (error) {
                console.warn('[faviconBadge] failed to render badge', error);
            }
        };

        if (pendingCount !== undefined) {
            applyCount(pendingCount);
            pendingCount = undefined;
        }
    };

    init().catch((error) => {
        console.warn('[faviconBadge] init failed, favicon badge disabled', error);
        applyCount = () => {};
    });

    return count => applyCount(count);
};
